import React, { FC, useCallback, useEffect } from 'react';
import {
  Control,
  useFieldArray,
  UseFormResetField,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { CalendarIcon, CloseIcon } from '../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import Button from '../../../../../../components/button/index';
import { OverlayDatePicker as DatePicker } from '../../../../../../components/date-picker/index';
import { FormInput } from '../../../../../../components/input/index';
import { useFormat } from '../../../../../../hooks/index';
import { SharePlanType } from '../../../../../../types/pool-plans.types';
import { formatRemainingExercisable } from '../calculate-remaining-exercisable.util';
import { GrantCombobox, StakeholderCombobox, TableHeaderItem } from '../form-components/index';
import { FormSchema } from '../terminated-exercise-form.validation';

export type BspceProps = {
  companyId: string;
  control: Control<FormSchema>;
  resetField: UseFormResetField<FormSchema>;
  setFormData: UseFormSetValue<FormSchema>;
};

export const Bspce: FC<BspceProps> = ({ control, setFormData, companyId, resetField }) => {
  const { stepOne } = useWatch<FormSchema>({ control });
  const { format } = useFormat();

  const {
    fields: bspceItemsFields,
    append: bspceItemsAppend,
    remove: bspceItemsRemove,
  } = useFieldArray({
    control,
    name: 'stepOne.bspce.bspceItems',
  });

  const handleRemoveBspceItems = useCallback(
    (index: number) => {
      bspceItemsRemove(index);
    },
    [bspceItemsRemove],
  );

  const handleAppendBspceItems = useCallback(() => {
    const defaultDate = new Date();
    bspceItemsAppend({
      stakeholder: { id: '', fullName: '' },
      grant: {
        id: '',
        grantItem: {
          plan: { strikePrice: 0, conversionRatio: 1, pool: { shareClass: { name: '' } } },
        },
        exercisedCount: 0,
        vestedSharesCount: 0,
      },
      date: defaultDate,
      exercised: 0,
      returnedToPool: 0,
    });
  }, [bspceItemsAppend]);

  const handleStakeholderChange = (index: number) => {
    resetField(`stepOne.bspce.bspceItems.${index}.grant`);
  };

  const calculateBalance = useCallback(
    (index: number) => {
      const item = stepOne?.bspce?.bspceItems?.[index];
      const vestedShares = item?.grant?.vestedSharesCount || 0;
      const exercisedShares = item?.grant?.exercisedCount || 0;
      const exercised = item?.exercised || 0;
      const returnedToPool = item?.returnedToPool || 0;

      return formatRemainingExercisable(vestedShares, exercisedShares, exercised, returnedToPool);
    },
    [stepOne?.bspce?.bspceItems],
  );

  useEffect(() => {
    if (bspceItemsFields.length === 0) {
      handleAppendBspceItems();
    }
  }, [handleAppendBspceItems, bspceItemsFields.length]);

  const showRemoveButton = bspceItemsFields.length > 1;

  return (
    <>
      <div className="flex w-[1420px] flex-col rounded-lg border-[1px] border-gray-100 bg-white">
        <div
          className={twMerge(
            'grid w-full rounded-t-lg',
            showRemoveButton
              ? 'grid-cols-[repeat(3,184px)_repeat(5,minmax(164px,1fr))_repeat(1,48px)]'
              : 'grid-cols-[repeat(3,184px)_repeat(5,minmax(164px,1fr))]',
          )}
        >
          <TableHeaderItem showTooltip text={<AppFormattedMessage id="STAKEHOLDER" />} />
          <TableHeaderItem showTooltip text={<AppFormattedMessage id="GRANT" />} />
          <TableHeaderItem showTooltip text={<AppFormattedMessage id="EXERCISE_DATE" />} />
          <TableHeaderItem text={<AppFormattedMessage id="SHARE_CLASS" />} />
          <TableHeaderItem text={<AppFormattedMessage id="STRIKE_PRICE" />} />
          <TableHeaderItem text={<AppFormattedMessage id="EXERCISED_OPTIONS" />} variant="dark" />
          <TableHeaderItem text={<AppFormattedMessage id="ISSUED_SHARES" />} variant="dark" />
          <TableHeaderItem text={<AppFormattedMessage id="RETURNED_TO_POOL" />} variant="dark" />
          {showRemoveButton && <TableHeaderItem text=" " />}
        </div>
        {bspceItemsFields.map((field, index) => (
          <div
            className={twMerge(
              'grid w-full border-b-[1px] border-b-gray-100',
              showRemoveButton
                ? 'grid-cols-[repeat(3,184px)_repeat(5,minmax(164px,1fr))_repeat(1,48px)]'
                : 'grid-cols-[repeat(3,184px)_repeat(5,minmax(164px,1fr))]',
            )}
            key={field.id}
          >
            <div className="px-4 pb-10 pt-7">
              <StakeholderCombobox
                companyId={companyId}
                control={control}
                handleStakeholderChange={() => handleStakeholderChange(index)}
                name={`stepOne.bspce.bspceItems.${index}.stakeholder`}
                planType={SharePlanType.BSPCE}
              />
            </div>
            <div className="px-4 pb-10 pt-7">
              <GrantCombobox
                companyId={companyId}
                control={control}
                name={`stepOne.bspce.bspceItems.${index}.grant`}
                selectedGrantIds={stepOne?.bspce?.bspceItems?.map((item) => item?.grant?.id || '')}
                sharePlanType={SharePlanType.BSPCE}
                stakeholderId={stepOne?.bspce?.bspceItems?.[index]?.stakeholder?.id || ''}
              />
            </div>
            <div className="px-4 pb-10 pt-7">
              <DatePicker
                onSelect={(date) => setFormData(`stepOne.bspce.bspceItems.${index}.date`, date)}
                value={stepOne?.bspce?.bspceItems?.[index]?.date}
                wrapperClassName="w-full"
              >
                <FormInput
                  autoComplete="off"
                  control={control}
                  disabledInput
                  icon={<CalendarIcon className="size-5" iconColor="#344054" />}
                  inputClassName="h-10"
                  name={`stepOne.bspce.bspceItems.${index}.date`}
                  transform={(value) => {
                    return format(value, 'dd/MM/yyyy');
                  }}
                  wrapperClassName="w-full"
                />
              </DatePicker>
            </div>
            <div className="flex items-center px-4 pb-10 pt-7">
              <span className="text-sm font-[450] text-gray-700">
                {
                  stepOne?.bspce?.bspceItems?.[index]?.grant?.grantItem?.plan?.pool?.shareClass
                    ?.name
                }
              </span>
            </div>
            <div className="flex flex-col gap-2 px-4 pb-4 pt-7">
              <FormInput
                control={control}
                disabled={true}
                inputClassName="h-10"
                name={`stepOne.bspce.bspceItems.${index}.grant.grantItem.plan.strikePrice`}
                shouldFormatNumber
                wrapperClassName="w-full"
              />
              <span className="block max-w-[164px] overflow-hidden truncate text-ellipsis whitespace-nowrap text-xs font-[450] text-gray-600">
                <AppFormattedMessage id="TOTAL_COST" /> ${' '}
                {(
                  (stepOne?.bspce?.bspceItems?.[index]?.grant?.grantItem?.plan?.strikePrice || 0) *
                  (stepOne?.bspce?.bspceItems?.[index]?.exercised ?? 0)
                ).toLocaleString('en-US')}
              </span>
            </div>
            <div className="flex flex-col gap-2 px-4 pb-4 pt-7">
              <FormInput
                control={control}
                inputClassName="h-10"
                name={`stepOne.bspce.bspceItems.${index}.exercised`}
                numberOnly
                shouldDisplayZero
                shouldFormatNumber
                wrapperClassName="w-full"
              />
              <span className="text-xs font-[450] text-gray-600">
                <AppFormattedMessage id="AVAILABLE" /> {calculateBalance(index)}
              </span>
            </div>
            <div className="flex flex-col gap-2 px-4 pb-4 pt-7">
              <div className="max-w-[164px] rounded-t-[4px] bg-gray-50 px-3 py-2">
                <span className="block w-full overflow-hidden truncate text-ellipsis whitespace-nowrap text-sm font-[450] text-gray-700">
                  {(
                    (stepOne?.bspce?.bspceItems?.[index]?.exercised || 0) *
                    (stepOne?.bspce?.bspceItems?.[index]?.grant?.grantItem?.plan?.conversionRatio ||
                      1)
                  ).toLocaleString('en-US')}
                </span>
              </div>
              <span className="text-xs font-[450] text-gray-600">
                <AppFormattedMessage id="CONVERSION" />{' '}
                {stepOne?.bspce?.bspceItems?.[index]?.grant?.grantItem?.plan?.conversionRatio || 1}x
              </span>
            </div>
            <div className="flex flex-col gap-2 px-4 pb-4 pt-7">
              <FormInput
                control={control}
                inputClassName="h-10"
                name={`stepOne.bspce.bspceItems.${index}.returnedToPool`}
                numberOnly
                shouldDisplayZero
                shouldFormatNumber
                wrapperClassName="w-full"
              />
              <span className="text-xs font-[450] text-gray-600">
                <AppFormattedMessage id="AVAILABLE" /> {calculateBalance(index)}
              </span>
            </div>
            {showRemoveButton && (
              <div>
                <Button
                  className="flex h-full w-full items-center justify-center"
                  onClick={() => handleRemoveBspceItems(index)}
                  styleType="NONE"
                >
                  <CloseIcon />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {bspceItemsFields.length < 10 && (
        <div className="sticky bottom-[76px] z-10 w-full bg-white">
          <Button
            className="w-fit px-4 py-2 text-sm font-[450] text-brand-700 underline disabled:border-transparent disabled:bg-transparent"
            onClick={handleAppendBspceItems}
            styleType="NONE"
          >
            + <AppFormattedMessage id="ADD_EXERCISING" />
          </Button>
        </div>
      )}
    </>
  );
};
